import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styles from '../styles/Home.module.css';
import profileImage from '../assets/headshot.jpg';
import workImage1 from '../assets/work1.jpg';
import workImage2 from '../assets/work2.jpg';
import workImage3 from '../assets/work3.jpg';

// Animation Variants for "Pop Art" feel
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const blockVariants = {
  hidden: { y: 20, opacity: 0, scale: 0.95 },
  visible: {
    y: 0, opacity: 1, scale: 1,
    transition: { type: "spring", stiffness: 120 }
  }
};

function Home() {
  const [greeting, setGreeting] = useState('Hi');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 18) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
  }, []);

  return (
    <motion.div
      className={styles.container}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Decorative Background Shapes */}
      <div className={styles.shapeOne}></div>
      <div className={styles.shapeTwo}></div>

      {/* Hero Section */}
      <section className={styles.hero}>
        <motion.div className={styles.heroContent} variants={blockVariants}>
          <h1 className={styles.heroTitle}>I'm Alex Mahadevan</h1>
          <p className={styles.heroSubtitle}>
            I'm a journalist, data guy, trainer and researcher working at the intersection of generative AI, media literacy and misinformation. I'm also a drummer, perpetually injured runner and Very Bad golfer.
          </p>
        </motion.div>

        <motion.div className={styles.imageContainer} variants={blockVariants}>
          <img src={profileImage} alt="Alex Mahadevan" className={styles.profileImage} />
        </motion.div>
      </section>

      {/* About Section */}
      <section className={styles.about}>
        <div className={styles.aboutBox}>
          <h2 className={styles.headerTitle}>What I do</h2>
          <p>
            I'm currently the director of <a href="https://poynter.org/mediawise" target="_blank">MediaWise</a>, a digital media literacy program based out out of the Poynter Institute.
            I'm also <a href="https://www.poynter.org/news-release/2024/kristen-hare-local-news-alex-mahadevan-ai/" target="_blank">on faculty</a>, where I lead our <a href="https://www.poynter.org/tech-tools/artificial-intelligence/2025/poynter-launches-ai-innovation-lab-to-house-its-growing-ai-portfolio/" target="_blank">AI Innovation Lab</a> and run workshops on emerging technologies, verification and OSINT.
            I spend more time than I'd care to admit studying X's <a href="https://www.poynter.org/fact-checking/2023/why-twitters-community-notes-feature-mostly-fails-to-combat-misinformation/" target="_blank">Community Notes</a>.
          </p>
        </div>
      </section>

      {/* Work Showcase */}
      <section className={styles.work}>
        <h2 className={styles.headerTitle} style={{ transform: 'rotate(1deg)' }}>Stuff I've done</h2>
        <div className={styles.workGrid}>
          <motion.a href="https://www.poynter.org/poynter-ai-leadership/" target="_blank" rel="noopener noreferrer" className={styles.workItem} variants={blockVariants}>
            <div className={styles.workImageContainer}>
              <img src={workImage2} alt="AI Innovation Lab" />
            </div>
            <div className={styles.workText}>
              <p className={styles.projectTitle}>The Poynter AI Innovation Lab</p>
              <p className={styles.projectSubText}>
                What started as some generative AI trainings I did for fun in 2023 evolved into multiple AI initiatives at Poynter. Now I lead Poynter's AI Innovation Lab.
              </p>
            </div>
          </motion.a>

          <motion.a href="https://www.poynter.org/mediawise/programs/seniors/" target="_blank" rel="noopener noreferrer" className={styles.workItem} variants={blockVariants}>
            <div className={styles.workImageContainer}>
              <img src={workImage1} alt="MediaWise for Seniors" />
            </div>
            <div className={styles.workText}>
              <p className={styles.projectTitle}>MediaWise for Seniors</p>
              <p className={styles.projectSubText}>
                My first program at Poynter was an AARP partnership that included courses, videos and social media PSAs — plus Joan Lunden — teaching older adults how to fight misinformation.
              </p>
            </div>
          </motion.a>

          <motion.a href="/training" target="_blank" rel="noopener noreferrer" className={styles.workItem} variants={blockVariants}>
            <div className={styles.workImageContainer}>
              <img src={workImage3} alt="International Training" />
            </div>
            <div className={styles.workText}>
              <p className={styles.projectTitle}>Tunisia to Bangladesh: Lots of international training</p>
              <p className={styles.projectSubText}>
                I've spent the last five years teaching media literacy, verification, OSINT and generative AI to a variety of audiences around the world.
              </p>
            </div>
          </motion.a>
        </div>
      </section>

      {/* Call-to-Action */}
      <section className={styles.cta}>
        <h2>Reach out</h2>
        <p>I'm always down to chat about media literacy, misinformation and generative AI. Find me at any of these places to partner on something cool, put me on a panel or for an interview.</p>
        <div className={styles.ctaLinks}>
          <a href="mailto:alexmahadevan@gmail.com" className={styles.ctaLink}>
            📧 Email me
          </a>
          <a
            href="https://www.linkedin.com/in/alexmahadevan/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaLink}
          >
            💼 Connect on LinkedIn
          </a>
          <a
            href="https://bsky.app/profile/alexmahadevan.com"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaLink}
          >
            🦋 Follow me on Bluesky
          </a>
        </div>
      </section>
    </motion.div>
  );
}

export default Home;
